import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useCreateSignature, useUpdateSignature } from '@/hooks/useSignatures'
import { Signature, SignatureForm as SignatureFormType } from '@/types'
import { Upload } from 'lucide-react'

const signatureSchema = z.object({
  // 基础信息
  name: z.string().min(2, '签名名称至少2个字符').max(50, '签名名称不能超过50个字符'),
  type: z.enum(['COMPANY', 'WEBSITE', 'APP', 'WECHAT', 'BRAND', 'TRADEMARK', 'GOVERNMENT'], { required_error: '请选择签名类型' }),
  usageType: z.enum(['SELF','OTHERS']).default('SELF'),
  
  // 申请人信息（运营商要求）
  applicantName: z.string().min(2, '申请人姓名至少2个字符'),
  applicantPhone: z.string().regex(/^1[3-9]\d{9}$/, '请输入正确的手机号码'),
  applicantIdCard: z.string().regex(/^\d{17}[\dXx]$/, '请输入正确的身份证号码'),
  
  // 申请材料
  certificate: z.instanceof(File).optional(),
  authorization: z.instanceof(File).optional(),
  
  // 申请说明
  applyReason: z.string().min(10, '申请说明至少10个字符').max(500, '申请说明不能超过500个字符'),
})

interface SignatureFormProps {
  signature?: Signature
  onSuccess?: () => void
}

export default function SignatureForm({ signature, onSuccess }: SignatureFormProps) {
  const isEdit = !!signature
  const createSignatureMutation = useCreateSignature()
  const updateSignatureMutation = useUpdateSignature()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm<SignatureFormType>({
    resolver: zodResolver(isEdit ? signatureSchema.omit({ certificate: true }) : signatureSchema),
    defaultValues: signature ? {
      name: signature.name,
      type: signature.type,
      usageType: signature.usageType,
      applicantName: signature.applicantName,
      applicantPhone: signature.applicantPhone,
      applicantIdCard: signature.applicantIdCard,
      applyReason: signature.applyReason,
    } : undefined,
  })

  const usageType = watch('usageType', 'SELF')
  const type = watch('type')
  const name = watch('name')

  const onSubmit = async (data: SignatureFormType) => {
    try {
      if (isEdit) {
        await updateSignatureMutation.mutateAsync({
          id: signature.id,
          data,
        })
      } else {
        await createSignatureMutation.mutateAsync(data)
      }
      onSuccess?.()
    } catch (error) {
      console.error('Form submission error:', error)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setValue('certificate', file)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {/* 基础信息 */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900">基础信息</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">签名名称 *</Label>
            <Input
              id="name"
              {...register('name')}
              placeholder="请输入签名名称"
            />
            {errors.name && (
              <p className="text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>签名预览</Label>
            <div className="p-3 bg-gray-50 border rounded-md">
              <p className="text-sm font-mono">
                {watch('name') ? `【${watch('name')}】` : '【签名名称】'}
              </p>
            </div>
            <p className="text-sm text-muted-foreground">
              签名将以此格式显示在短信开头
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>签名类型 *</Label>
            <Select
              onValueChange={(value) => setValue('type', value as any)}
              defaultValue={signature?.type}
            >
              <SelectTrigger>
                <SelectValue placeholder="请选择签名类型" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="COMPANY">🏢 公司</SelectItem>
                <SelectItem value="WEBSITE">🌐 网站</SelectItem>
                <SelectItem value="APP">📱 APP</SelectItem>
                <SelectItem value="WECHAT">💬 微信公众号</SelectItem>
                <SelectItem value="BRAND">🏷️ 品牌</SelectItem>
                <SelectItem value="TRADEMARK">®️ 商标</SelectItem>
                <SelectItem value="GOVERNMENT">🏛️ 政府机构</SelectItem>
              </SelectContent>
            </Select>
            {errors.type && (
              <p className="text-sm text-red-600">{errors.type.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>使用类型 *</Label>
            <Select
              onValueChange={(value) => setValue('usageType', value as 'SELF' | 'OTHERS')}
              defaultValue={signature?.usageType || 'SELF'}
            >
              <SelectTrigger>
                <SelectValue placeholder="请选择使用类型" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="SELF">自用</SelectItem>
                <SelectItem value="OTHERS">他用</SelectItem>
              </SelectContent>
            </Select>
            {errors.usageType && (
              <p className="text-sm text-red-600">{errors.usageType.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* 申请人信息 */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900">申请人信息</h3>
        <p className="text-sm text-muted-foreground">
          请填写真实有效的申请人信息，运营商将进行核实
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="applicantName">申请人姓名 *</Label>
            <Input
              id="applicantName"
              {...register('applicantName')}
              placeholder="请输入真实姓名"
            />
            {errors.applicantName && (
              <p className="text-sm text-red-600">{errors.applicantName.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="applicantPhone">联系电话 *</Label>
            <Input
              id="applicantPhone"
              {...register('applicantPhone')}
              placeholder="请输入手机号码"
            />
            {errors.applicantPhone && (
              <p className="text-sm text-red-600">{errors.applicantPhone.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="applicantIdCard">身份证号 *</Label>
            <Input
              id="applicantIdCard"
              {...register('applicantIdCard')}
              placeholder="请输入身份证号码"
            />
            {errors.applicantIdCard && (
              <p className="text-sm text-red-600">{errors.applicantIdCard.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* 申请材料 */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900">申请材料</h3>
        <p className="text-sm text-muted-foreground">
          请上传相关资质证明文件，支持JPG、PNG、PDF格式，单个文件不超过5MB
        </p>
        
        {!isEdit && (
          <div className="space-y-3">
                          <div className="space-y-2">
                <Label htmlFor="certificate">资质证明文件 *</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                  <Upload className="mx-auto h-8 w-8 text-gray-400" />
                  <div className="mt-2">
                    <Label htmlFor="certificate" className="cursor-pointer">
                      <span className="text-blue-600 hover:text-blue-500 font-medium">点击上传文件</span>
                      <span className="text-gray-500"> 或拖拽文件到此处</span>
                    </Label>
                    <Input
                      id="certificate"
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    根据签名类型上传相应资质：营业执照、网站备案、APP证书等
                  </p>
                </div>
              {errors.certificate && (
                <p className="text-sm text-red-600">{errors.certificate.message}</p>
              )}
            </div>

                          {usageType === 'OTHERS' && (
                <div className="space-y-2">
                  <Label htmlFor="authorization">授权委托书 *</Label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                    <Upload className="mx-auto h-8 w-8 text-gray-400" />
                    <div className="mt-2">
                      <Label htmlFor="authorization" className="cursor-pointer">
                        <span className="text-blue-600 hover:text-blue-500 font-medium">点击上传文件</span>
                        <span className="text-gray-500"> 或拖拽文件到此处</span>
                      </Label>
                      <Input
                        id="authorization"
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e)=>{ const f=e.target.files?.[0]; if(f){ setValue('authorization', f) }}}
                        className="hidden"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      请上传加盖公章的授权委托书，证明您有权使用该签名
                    </p>
                  </div>
                {errors.authorization && (
                  <p className="text-sm text-red-600">{errors.authorization.message as any}</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 申请说明 */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900">申请说明</h3>
        <p className="text-sm text-muted-foreground">
          请详细说明签名的用途、使用场景和相关资质情况
        </p>
        
        <div className="space-y-2">
          <Label htmlFor="applyReason">申请说明 *</Label>
          <Textarea
            id="applyReason"
            {...register('applyReason')}
            placeholder="请详细说明：1. 签名用途和使用场景 2. 相关资质情况 3. 预计发送量 4. 其他需要说明的情况"
            rows={3}
          />
          {errors.applyReason && (
            <p className="text-sm text-red-600">{errors.applyReason.message}</p>
          )}
        </div>
      </div>

      {/* 审核说明 */}
      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-start space-x-2">
          <div className="w-4 h-4 bg-blue-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
            <span className="text-blue-600 text-xs">ℹ</span>
          </div>
          <div>
            <h4 className="font-medium text-blue-900 mb-1 text-sm">审核说明</h4>
            <ul className="text-xs text-blue-800 space-y-0.5">
              <li>• <strong>签名内容</strong>：需与您的业务相关，不得包含违法违规内容</li>
              <li>• <strong>企业签名</strong>：需提供营业执照、组织机构代码证等相关资质</li>
              <li>• <strong>网站签名</strong>：需提供ICP备案证明、域名证书等</li>
              <li>• <strong>APP签名</strong>：需提供软件著作权证书、应用商店上架证明等</li>
              <li>• <strong>他用签名</strong>：需提供加盖公章的授权委托书</li>
              <li>• <strong>审核时间</strong>：通常为1-3个工作日，请耐心等待</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 提交按钮 */}
      <div className="flex justify-end space-x-2 pt-3 border-t border-gray-200">
        <Button type="submit" disabled={isSubmitting} className="px-6">
          {isSubmitting ? '提交中...' : isEdit ? '更新签名' : '提交申请'}
        </Button>
      </div>
    </form>
  )
}